import React from 'react';
import { useFormContext } from 'react-hook-form';
import { CommitmentType, CurrencyCode } from 'further-types/lp/vehicle';
import EqualisationFooter from '../EqualisationFooter';
import { useParams } from 'react-router-dom';
import { useLayoutContext } from 'contexts/LayoutContext';
import { useEqualisationSection } from '../EqualisationContext';
import api from 'lib/trpcClient';
import { EqualisationSection } from 'further-types/lp/equalisation';
import { EqualisationGeneralCapitalCallsFormValues } from './FormWrapper';
import GeneralCapitalCallCard from './GeneralCapitalCallCard';
import { sumBy } from 'lodash';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import { getQueryKey } from '@trpc/react-query';
import { Alert } from '@mui/material';
import EqualisationPage from '../EqualisationPage';
import Table, { Column as TableColumn } from 'further-ui/components/Table';
import { numberToCurrencyString } from 'further-ui/utils';
import { useExpandedRows } from 'hooks/ui/useExpandedRows';
import { match } from 'ts-pattern';
import { AllocationRule } from 'further-types/lp/allocations';
import { amber } from '@mui/material/colors';
import ColorIndicatorWithText from 'components/new/ColorIndicatorWithText';

interface GeneralCapitalCallsContentProps {
  baseCurrency: CurrencyCode;
}

const GeneralCapitalCallsContent: React.FC<GeneralCapitalCallsContentProps> = ({
  baseCurrency,
}) => {
  const { getValues, watch } =
    useFormContext<EqualisationGeneralCapitalCallsFormValues>();
  const generalCapitalCalls = watch('generalCapitalCalls');
  const {
    previousSection,
    currentSection,
    nextSection,
    goToNextSection,
    goToPreviousSection,
    goToListPage,
  } = useEqualisationSection();
  const { id } = useParams<{ id?: string }>();
  const { expandedRows, toggleRowExpansion } = useExpandedRows();
  const { selectedVehicleId, vehicle } = useLayoutContext();
  const equalisationActionAllocationsQueryKey = getQueryKey(
    api.equalisations.getEqualisationGeneralCapitalCalls,
    {
      equalisationId: id ?? '',
      vehicleId: selectedVehicleId ?? '',
    },
  );
  const updateEqualisation = api.equalisations.update.useMutation(
    useHandleTrpcMutation()({
      invalidations: [equalisationActionAllocationsQueryKey],
    }),
  );

  const allocationsAreValid = generalCapitalCalls?.every(
    (generalCapitalCall) => {
      const includedAllocations =
        generalCapitalCall.newInvestorBreakdown.filter(
          (item) => !item.excludeFromAllocation,
        );
      const totalAllocationAmountLcy = sumBy(includedAllocations, (item) =>
        Number(item.calledFundsLcy),
      );
      return (
        Math.abs(totalAllocationAmountLcy - generalCapitalCall.totalAmountLcy) <
        0.01
      );
    },
  );

  const handleNext = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: nextSection ?? EqualisationSection.Summary,
        generalCapitalCalls: getValues('generalCapitalCalls'),
      },
      {
        onSuccess: () => {
          goToNextSection();
        },
      },
    );
  };

  const handleSaveAsDraft = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection,
        generalCapitalCalls: getValues('generalCapitalCalls'),
      },
      {
        onSuccess: () => {
          goToListPage();
        },
      },
    );
  };

  const handlePrevious = () => {
    updateEqualisation.mutate(
      {
        equalisationId: id ?? '',
        vehicleId: selectedVehicleId ?? '',
        currentSection: previousSection ?? EqualisationSection.Commitments,
        generalCapitalCalls: getValues('generalCapitalCalls'),
      },
      {
        onSuccess: () => {
          goToPreviousSection();
        },
      },
    );
  };

  const rows = generalCapitalCalls.map((generalCapitalCall) => ({
    id: generalCapitalCall.actionId,
    ...generalCapitalCall,
  }));

  const columns: Array<TableColumn<(typeof rows)[number]>> = [
    {
      key: 'name',
      label: 'Name',
      render: ({ name, allocationRule }) => (
        <ColorIndicatorWithText
          subtext={match(allocationRule)
            .with(AllocationRule.Manual, () => 'Manually allocated')
            .otherwise(() => 'Allocated by commitment and close date')}
          text={name}
          colorKey={{ name }}
        />
      ),
    },
    {
      key: 'ledgerDate',
      label: 'Date',
      render: (row) => row.ledgerDate.toReadable(),
    },
    {
      key: 'totalAmountLcy',
      label: 'Amount',
      render: (row) =>
        numberToCurrencyString(row.totalAmountLcy, { currency: baseCurrency }),
    },
  ];

  return (
    <EqualisationPage
      actionBar={
        <EqualisationFooter
          handleNext={handleNext}
          handlePrevious={handlePrevious}
          handleSaveAsDraft={handleSaveAsDraft}
          loading={updateEqualisation.isPending}
          disabled={!allocationsAreValid}
        />
      }
      subheading="Equalise general capital calls."
    >
      {vehicle?.commitmentType === CommitmentType.CapitalAndLoan && (
        <Alert severity="info">
          This step will calculate the total gross amount to be called from each
          investor. Where an investor has a capital contribution to be called,
          their capital contribution will be deducted from their total gross
          call amount.
        </Alert>
      )}
      <Table
        isExpandableRow={() => true}
        isExpandedRow={({ id }) => expandedRows.has(id)}
        onRowExpand={({ id }) => {
          toggleRowExpansion(id);
        }}
        onRowClick={({ id }) => {
          toggleRowExpansion(id);
        }}
        getRowBackgroundColor={({ allocationRule }) =>
          match(allocationRule)
            .with(AllocationRule.Manual, () => amber[50])
            .otherwise(() => undefined)
        }
        getExpandedContent={(row, _, rowIndex) => (
          <GeneralCapitalCallCard
            key={rowIndex}
            actionIndex={rowIndex}
            generalCapitalCall={row}
            baseCurrency={baseCurrency}
          />
        )}
        columns={columns}
        rows={rows}
      />
    </EqualisationPage>
  );
};

export default GeneralCapitalCallsContent;
